import { useState } from 'react';

export interface ShareOrderItem {
  name: string;
  quantity: number;
  price: number;
}

interface KakaoShareButtonProps {
  title: string;
  description: string;
  orderNumber?: string;
  items?: ShareOrderItem[];
  totalAmount?: string;
  paymentMethod?: string;
  className?: string;
}

export default function KakaoShareButton({
  title,
  description,
  orderNumber,
  items,
  totalAmount,
  paymentMethod,
  className = '',
}: KakaoShareButtonProps) {
  const [shareState, setShareState] = useState<'idle' | 'copied' | 'shared'>('idle');
  const [previewOpen, setPreviewOpen] = useState(false);

  const formatPrice = (n: number) => n.toLocaleString('ko-KR');

  const buildFullText = () => {
    const lines: string[] = [title, ''];
    if (orderNumber) lines.push(`📋 주문번호: ${orderNumber}`);
    if (paymentMethod) lines.push(`💳 결제방법: ${paymentMethod}`);
    if (totalAmount) lines.push(`💰 결제금액: ${totalAmount}`);
    if (items && items.length > 0) {
      lines.push('');
      lines.push('🛍️ 주문 상품');
      items.forEach((item) => {
        lines.push(`  • ${item.name} × ${item.quantity} (${formatPrice(item.price * item.quantity)}원)`);
      });
    }
    if (description) {
      const extra = description.split('\n').filter(Boolean);
      if (extra.length > 0) { lines.push(''); extra.forEach((l) => lines.push(l)); }
    }
    lines.push('');
    lines.push('— Global AI Trade');
    lines.push(window.location.origin);
    return lines.join('\n');
  };

  const handleShare = async () => {
    const shareText = buildFullText();

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Global AI Trade 주문 내역',
          text: shareText,
          url: window.location.origin,
        });
        setShareState('shared');
        setTimeout(() => setShareState('idle'), 2500);
      } catch {
        // 사용자가 취소한 경우 무시
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(shareText);
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 2500);
    } catch {
      setShareState('idle');
    }
  };

  const previewLines: string[] = [];
  if (orderNumber) previewLines.push(`주문번호  ${orderNumber}`);
  if (paymentMethod) previewLines.push(`결제방법  ${paymentMethod}`);
  if (totalAmount) previewLines.push(`결제금액  ${totalAmount}`);

  return (
    <div className={`w-full ${className}`}>
      {/* 공유 미리보기 카드 */}
      <div className="mb-3 border border-yellow-200 rounded-2xl overflow-hidden">
        {/* 미리보기 헤더 */}
        <button
          type="button"
          onClick={() => setPreviewOpen((p) => !p)}
          className="w-full flex items-center justify-between px-4 py-3 bg-yellow-50 hover:bg-yellow-100 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 flex items-center justify-center bg-yellow-400 rounded-full flex-shrink-0">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="text-yellow-900">
                <path d="M12 3C6.477 3 2 6.477 2 10.5c0 2.63 1.57 4.94 3.93 6.32L5 21l4.27-2.27C10.14 18.9 11.06 19 12 19c5.523 0 10-3.477 10-7.5S17.523 3 12 3z" />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-xs font-bold text-yellow-900 leading-tight">주문 내역 공유 미리보기</p>
              <p className="text-[10px] text-yellow-700 leading-tight">카카오톡으로 공유할 내용을 확인하세요</p>
            </div>
          </div>
          <i
            className={`ri-arrow-down-s-line text-yellow-600 w-5 h-5 flex items-center justify-center transition-transform duration-200 ${previewOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* 미리보기 본문 */}
        {previewOpen && (
          <div className="bg-white px-4 py-3.5 border-t border-yellow-100">
            {/* 카카오톡 채팅 버블 스타일 */}
            <div className="bg-yellow-50 rounded-2xl rounded-tl-sm p-3.5 border border-yellow-200 max-w-[85%]">
              <p className="text-xs font-bold text-gray-900 mb-2 leading-snug">{title}</p>
              {previewLines.length > 0 && (
                <div className="space-y-1 mb-2">
                  {previewLines.map((line, i) => {
                    const [label, ...rest] = line.split('  ');
                    return (
                      <div key={i} className="flex gap-2 text-xs">
                        <span className="text-gray-500 whitespace-nowrap">{label}</span>
                        <span className="font-semibold text-gray-900">{rest.join('  ')}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {items && items.length > 0 && (
                <div className="border-t border-yellow-200 pt-2 mt-2">
                  <p className="text-[10px] font-bold text-gray-500 mb-1.5">🛍️ 주문 상품</p>
                  <div className="space-y-0.5">
                    {items.slice(0, 3).map((item, i) => (
                      <p key={i} className="text-xs text-gray-700">
                        · {item.name} ×{item.quantity}
                        <span className="text-gray-500 ml-1">{formatPrice(item.price * item.quantity)}원</span>
                      </p>
                    ))}
                    {items.length > 3 && (
                      <p className="text-xs text-gray-400">· 외 {items.length - 3}건 더...</p>
                    )}
                  </div>
                </div>
              )}
              <p className="text-[10px] text-gray-400 mt-2">— Global AI Trade</p>
            </div>
            <p className="text-[10px] text-gray-400 mt-2 text-center">
              위 내용이 카카오톡 메시지로 전송됩니다
            </p>
          </div>
        )}
      </div>

      {/* 공유 버튼 */}
      <button
        type="button"
        onClick={handleShare}
        className={`w-full flex items-center justify-center gap-2.5 py-3.5 rounded-full font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${
          shareState === 'shared'
            ? 'bg-yellow-300 text-yellow-900'
            : shareState === 'copied'
            ? 'bg-yellow-300 text-yellow-900'
            : 'bg-yellow-400 hover:bg-yellow-500 text-yellow-900'
        }`}
      >
        {shareState === 'shared' ? (
          <>
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-check-line text-yellow-900 w-5 h-5 flex items-center justify-center"></i>
            </div>
            카카오톡 공유 완료!
          </>
        ) : shareState === 'copied' ? (
          <>
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-file-copy-line text-yellow-900 w-5 h-5 flex items-center justify-center"></i>
            </div>
            복사 완료 — 카카오톡에 붙여넣기하세요
          </>
        ) : (
          <>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M12 3C6.477 3 2 6.477 2 10.5c0 2.63 1.57 4.94 3.93 6.32L5 21l4.27-2.27C10.14 18.9 11.06 19 12 19c5.523 0 10-3.477 10-7.5S17.523 3 12 3z" />
              </svg>
            </div>
            카카오톡으로 주문 내역 공유하기
          </>
        )}
      </button>
    </div>
  );
}
